import { createAsyncThunk } from "@reduxjs/toolkit"
import { APIError, AppThunkAPIType } from "../../../app/appTypes"
import { BlobResponse, deleteWithAuth, getBlobWithAuth, getWithAuth, postWithAuth } from "../../../http"
import { invalidateToken, selectBearerToken } from "../../user/userSlice"

/** Company report template assigned to an org unit (`api/report-templates`). */
export interface ReportTemplateDto {
    id: string
    documentType: string
    name: string
    structureNodeId: string
    structureNodeName: string | null
    fileName: string
    currentVersion: number
    sizeBytes: number
    uploadedBy: string
    uploadedAt: string
}

export interface ReportTemplateVersionDto {
    version: number
    uploadedAt: string
    uploadedBy: string
    sizeBytes: number
    remarks: string | null
    isCurrent: boolean
}

export interface TemplateIssue {
    code: string
    message: string
    tag: string | null
    location: string | null
    context: string | null
    suggestion: string | null
}

export interface TemplateValidationReport {
    errors: TemplateIssue[]
    warnings: TemplateIssue[]
    fixes: TemplateIssue[]
    tags: string[]
    isValid: boolean
}

export interface ReportTemplateUploadResultDto {
    template: ReportTemplateDto
    report: TemplateValidationReport
}

/**
 * Rejection of a template call. `report` is set when the backend refused the template itself
 * (400 "error-template-invalid"), so the UI can list every problem.
 */
export type ReportTemplateError =
    | APIError
    | { kind: "invalid-template", report: TemplateValidationReport }

type ReportTemplateThunkAPI = Omit<AppThunkAPIType, "rejectValue"> & { rejectValue: ReportTemplateError }

/** Document types that support a company report template. */
export const REPORT_DOCUMENT_TYPES = [
    { value: "Urs", labelId: "report-template-type-urs", labelFallback: "URS" },
    { value: "RiskAnalysis", labelId: "report-template-type-risk-analysis", labelFallback: "Analiza ryzyka" },
] as const

export const MAX_TEMPLATE_BYTES = 10 * 1024 * 1024

const BASE_URL = "api/report-templates"

const toTemplateError = (payload: any): ReportTemplateError => {
    const report = payload?.problem?.report as TemplateValidationReport | undefined
    if (report) {
        return { kind: "invalid-template", report }
    }
    return (payload as APIError) ?? { kind: "unknown" }
}

/**
 * Multipart POST. The shared http helpers are JSON-only, so this calls fetch directly with the
 * bearer token and mirrors their error shape ({ kind: "http", status, problem }).
 */
const postMultipart = async (
    url: string,
    body: FormData,
    { getState, dispatch, rejectWithValue }: { getState: () => unknown, dispatch: any, rejectWithValue: (v: ReportTemplateError) => any },
) => {
    try {
        const authHeader = selectBearerToken(getState() as any)
        const response = await fetch(process.env.REACT_APP_REST_API + url, {
            method: "POST",
            mode: "cors",
            headers: { ...authHeader },
            body,
        })

        const isJson = response.headers.get("content-type")?.includes("json") ?? false

        if (response.ok) {
            return isJson ? await response.json() : {}
        }

        if (response.status === 401) {
            dispatch(invalidateToken())
        }

        const problem = isJson ? await response.json() : undefined
        return rejectWithValue(toTemplateError({ kind: "http", status: response.status, problem }))
    } catch {
        return rejectWithValue({ kind: "connection" })
    }
}

const templateForm = (file: File, fields: Record<string, string | null | undefined> = {}) => {
    const fd = new FormData()
    fd.append("file", file)
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            fd.append(key, value)
        }
    })
    return fd
}

export const loadReportTemplates = createAsyncThunk<ReportTemplateDto[], void, AppThunkAPIType>(
    "nodeReportTemplates/loadAll",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: BASE_URL }))
        if (getWithAuth.fulfilled.match(result)) {
            return result.payload as ReportTemplateDto[]
        }
        return rejectWithValue((result.payload as APIError) ?? { kind: "unknown" })
    }
)

/** Dry run: normalize + validate without storing. */
export const validateReportTemplate = createAsyncThunk<
    TemplateValidationReport,
    { documentType: string, file: File },
    ReportTemplateThunkAPI
>(
    "nodeReportTemplates/validate",
    async ({ documentType, file }, thunkApi) =>
        postMultipart(`${BASE_URL}/${encodeURIComponent(documentType)}/validate`, templateForm(file), thunkApi)
)

/** Upload to an org unit; adds a new version when the unit already has a template of this type. */
export const uploadReportTemplate = createAsyncThunk<
    ReportTemplateUploadResultDto,
    { documentType: string, file: File, name: string, structureNodeId: string },
    ReportTemplateThunkAPI
>(
    "nodeReportTemplates/upload",
    async ({ documentType, file, name, structureNodeId }, thunkApi) =>
        postMultipart(
            `${BASE_URL}/${encodeURIComponent(documentType)}`,
            templateForm(file, { name, structureNodeId }),
            thunkApi,
        )
)

export const loadReportTemplateVersions = createAsyncThunk<ReportTemplateVersionDto[], string, AppThunkAPIType>(
    "nodeReportTemplates/versions",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `${BASE_URL}/${id}/versions` }))
        if (getWithAuth.fulfilled.match(result)) {
            return result.payload as ReportTemplateVersionDto[]
        }
        return rejectWithValue((result.payload as APIError) ?? { kind: "unknown" })
    }
)

export const restoreReportTemplateVersion = createAsyncThunk<
    ReportTemplateUploadResultDto,
    { id: string, version: number },
    ReportTemplateThunkAPI
>(
    "nodeReportTemplates/restore",
    async ({ id, version }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ url: `${BASE_URL}/${id}/versions/${version}/restore`, payload: {} }))
        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as ReportTemplateUploadResultDto
        }
        return rejectWithValue(toTemplateError(result.payload))
    }
)

export const deleteReportTemplate = createAsyncThunk<string, string, AppThunkAPIType>(
    "nodeReportTemplates/delete",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({ url: `${BASE_URL}/${id}` }))
        if (deleteWithAuth.fulfilled.match(result)) {
            return id
        }
        return rejectWithValue((result.payload as APIError) ?? { kind: "unknown" })
    }
)

export const downloadReportTemplate = createAsyncThunk<BlobResponse, { id: string, version?: number }, AppThunkAPIType>(
    "nodeReportTemplates/download",
    async ({ id, version }, { dispatch, rejectWithValue }) => {
        const query = version !== undefined ? `?version=${version}` : ""
        const result = await dispatch(getBlobWithAuth({ url: `${BASE_URL}/${id}/content${query}` }))
        if (getBlobWithAuth.fulfilled.match(result)) {
            return result.payload as BlobResponse
        }
        return rejectWithValue((result.payload as APIError) ?? { kind: "unknown" })
    }
)

/** The built-in template, as a starting point for customers' own templates. */
export const downloadSystemReportTemplate = createAsyncThunk<BlobResponse, string, AppThunkAPIType>(
    "nodeReportTemplates/downloadSystem",
    async (documentType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getBlobWithAuth({ url: `${BASE_URL}/system/${encodeURIComponent(documentType)}/content` }))
        if (getBlobWithAuth.fulfilled.match(result)) {
            return result.payload as BlobResponse
        }
        return rejectWithValue((result.payload as APIError) ?? { kind: "unknown" })
    }
)

export const saveBlob = ({ blobUrl, fileName }: BlobResponse, fallbackName: string) => {
    const link = document.createElement("a")
    link.href = blobUrl
    link.setAttribute("download", fileName && fileName !== "unknown" ? fileName : fallbackName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(blobUrl)
}

/** ProblemDetails title/detail, or null when the error has no readable message. */
export const problemMessage = (error: ReportTemplateError | undefined): string | null => {
    if (!error || error.kind !== "http" || !error.problem) {
        return null
    }
    return error.problem.detail || error.problem.title || null
}
